import { FC, useEffect, useRef } from 'react';
import { isPlacesConfigured, loadGooglePlaces } from '../../utils/googleMaps';

// item 207 — the standardized fields a selected Google place yields.
export interface ParsedPlace {
  name?: string;
  phone?: string;
  street?: string;
  unit?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

const componentParser = (place: any): ParsedPlace => {
  const components: any[] = place.address_components || [];
  const get = (type: string, short = false) => {
    const c = components.find((comp) => comp.types.includes(type));
    if (!c) return '';
    return short ? c.short_name : c.long_name;
  };
  const street = [get('street_number'), get('route')].filter(Boolean).join(' ');
  return {
    name: place.name,
    phone: place.formatted_phone_number || place.international_phone_number,
    street,
    unit: get('subpremise'),
    city: get('locality') || get('postal_town') || get('sublocality') || get('administrative_area_level_2'),
    state: get('administrative_area_level_1', true),
    zip: get('postal_code'),
    country: get('country', true),
  };
};

interface PlacesAttachProps {
  // the DOM id of the EXISTING form input to attach Google autocomplete onto
  // (TextInputControl renders id={name}, e.g. "name" or "serviceAddress.street")
  targetId: string;
  // 'address' = item 207-B address autocomplete; 'business' = item 207-C business lookup
  mode?: 'address' | 'business';
  onSelect: (place: ParsedPlace) => void;
}

// item 207/208 — attach Google Places autocomplete onto an EXISTING form field (the Name
// field for a business lookup, the Street field for an address) rather than a separate box.
// Renders nothing. If Google REJECTS the key it marks the input disabled / gm-err-autocomplete
// (the "Oops" error icon) — we strip that so the real field stays usable for manual entry.
const PlacesAttach: FC<PlacesAttachProps> = ({ targetId, mode = 'address', onSelect }) => {
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    if (!isPlacesConfigured()) return undefined;
    let cancelled = false;
    let autocomplete: any;
    let observer: MutationObserver | undefined;
    const el = document.getElementById(targetId) as HTMLInputElement | null;
    if (!el) return undefined;
    const origPlaceholder = el.placeholder;

    const preventEnter = (e: KeyboardEvent) => { if (e.key === 'Enter') e.preventDefault(); };
    el.addEventListener('keydown', preventEnter);

    // if Google rejects the key it disables + error-styles the input; undo so the field
    // remains a normal, typeable manual-entry field.
    const recover = () => {
      if (el.classList.contains('gm-err-autocomplete') || el.disabled || el.style.backgroundImage) {
        el.classList.remove('gm-err-autocomplete');
        el.disabled = false;
        el.style.backgroundImage = '';
        if (el.placeholder !== origPlaceholder) el.placeholder = origPlaceholder;
      }
    };

    loadGooglePlaces().then((places) => {
      if (cancelled || !places) return;
      autocomplete = new places.Autocomplete(el, {
        types: mode === 'business' ? ['establishment'] : ['address'],
        fields: ['address_components', 'name', 'formatted_phone_number', 'international_phone_number'],
      });
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place && (place.address_components || place.name)) onSelectRef.current(componentParser(place));
      });
      observer = new MutationObserver(recover);
      observer.observe(el, { attributes: true, attributeFilter: ['class', 'disabled', 'style'] });
      window.setTimeout(recover, 2000);
    });

    return () => {
      cancelled = true;
      el.removeEventListener('keydown', preventEnter);
      if (observer) observer.disconnect();
      const w = window as any;
      if (autocomplete && w.google?.maps?.event) w.google.maps.event.clearInstanceListeners(autocomplete);
    };
  }, [targetId, mode]);

  return null;
};

PlacesAttach.defaultProps = { mode: 'address' };

export default PlacesAttach;
